import React, { createContext, useContext, ReactNode, FC } from 'react'
import { ServerExperiments } from '@/experimentation'
import { PageGiveawayContent } from '@/services/GiveawayService/GiveawayService'
import { PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { PageGiveawayTabsItem } from '@/types/codegen-contentful'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useGiveawayState } from '../hooks/useGiveawayState'

interface GiveawayContextValue {
  // State
  content: PageGiveawayContent
  selectedIndex: number
  setSelectedIndex: (index: number) => void
  isBackgroundVideoPaused: boolean
  togglePauseBackgroundVideo: (value?: boolean | undefined) => void
  isShareModalOpen: boolean
  setIsShareModalOpen: (open: boolean) => void
  isScenePlayerOpen: boolean
  setIsScenePlayerOpen: (open: boolean) => void

  // Data
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  projectName: string
  experiments?: ServerExperiments

  // Computed values
  isActive: boolean
  entryVariantId: string

  // Actions
  scrollToProducts: () => void
  scrollToPrizes: () => void
  scrollToDetails: () => void
  scrollToFaq: () => void
  scrollToTab: (tabType: PageGiveawayTabsItem['__typename']) => void
  handleRefundInfoClick: () => void

  // Refs (for layout components)
  tabsContainerRef: React.RefObject<HTMLDivElement>
  mobileTicketContainerRef: React.RefObject<HTMLDivElement>
  desktopTicketContainerRef: React.RefObject<HTMLDivElement>
  heroContainerRef: React.RefObject<HTMLDivElement>
  detailsContainerRef: React.RefObject<HTMLDivElement>
  prizesContainerRef: React.RefObject<HTMLDivElement>
  faqContainerRef: React.RefObject<HTMLDivElement>
}

const GiveawayContext = createContext<GiveawayContextValue | null>(null)

export const useGiveawayContext = () => {
  const context = useContext(GiveawayContext)
  if (!context) {
    throw new Error('useGiveawayContext must be used within GiveawayContextProvider')
  }
  return context
}

interface GiveawayContextProviderProps {
  children: ReactNode
  content: PageGiveawayContent
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  experiments?: ServerExperiments
}

export const GiveawayContextProvider: FC<GiveawayContextProviderProps> = ({
  children,
  content,
  theatricalSlug,
  tickets,
  sweepstake,
  phase,
  experiments,
}) => {
  const giveawayState = useGiveawayState({
    content,
    theatricalSlug,
    tickets,
    sweepstake,
    phase,
    experiments,
  })

  return <GiveawayContext.Provider value={giveawayState}>{children}</GiveawayContext.Provider>
}
